/**
 * Centralized analytics helper for Résiliation Express.
 *
 * - Pushes events to window.dataLayer (consumed by both GTM and GA4 gtag).
 * - Idempotency: each named event fires at most once per key (memory + sessionStorage).
 * - Sanitizes payloads: never sends PII, phone, email, contract numbers, PayPal ids.
 * - Handles UTM/gclid capture on first visit and forwards on conversion events.
 * - Manages Google Consent Mode v2 updates from the cookie banner.
 */

const FIRED_KEY = "re_fired_events";
const UTM_KEY = "re_utm";
const CONSENT_KEY = "re_consent";

const PII_KEYS = new Set([
  "email", "phone", "first_name", "last_name", "address", "iban",
  "signature", "letter", "letter_content", "content", "raw",
  "paypal_order_id", "paypal_capture_id", "payer_id", "personal_info",
  "search_text", "query_text", "text",
]);

function loadFired() {
  try { return JSON.parse(sessionStorage.getItem(FIRED_KEY) || "{}"); }
  catch (e) { return {}; }
}
function saveFired(m) {
  try { sessionStorage.setItem(FIRED_KEY, JSON.stringify(m)); } catch (e) { /* ignore */ }
}

function sanitize(obj) {
  if (!obj || typeof obj !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (PII_KEYS.has(k)) continue;
    if (v == null) continue;
    if (typeof v === "string" && v.length > 120) out[k] = v.slice(0, 120);
    else if (typeof v === "object") continue; // no nested objects
    else out[k] = v;
  }
  return out;
}

/**
 * Push an event to the data layer.
 * @param {string} eventName
 * @param {object} params
 * @param {object} [opts]  {once:boolean, key?:string}
 */
export function pushAnalyticsEvent(eventName, params = {}, opts = {}) {
  if (typeof window === "undefined") return;
  const { once = true, key } = opts;
  const fireKey = key || eventName;
  const fired = loadFired();
  if (once && fired[fireKey]) return;

  const clean = sanitize(params);
  const utm = loadUtm();
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    event_timestamp: new Date().toISOString(),
    ...utm,
    ...clean,
  });

  if (once) {
    fired[fireKey] = Date.now();
    saveFired(fired);
  }
}

/**
 * SPA page view: send exactly one page_view per navigation.
 * Uses gtag if available; also pushes to dataLayer for GTM.
 */
let _lastPagePath = null;
export function trackPageView(path, title) {
  if (typeof window === "undefined") return;
  const p = path || window.location.pathname + window.location.search;
  if (_lastPagePath === p) return;
  _lastPagePath = p;
  const t = title || document.title;
  if (typeof window.gtag === "function") {
    window.gtag("event", "page_view", {
      page_path: p,
      page_title: t,
      page_location: window.location.origin + p,
    });
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: "spa_page_view", page_path: p, page_title: t });
}

// ---------- UTM ----------
export function captureUtmFromUrl() {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "gclid"];
  const captured = {};
  keys.forEach((k) => {
    const v = params.get(k);
    if (v) captured[k] = v.slice(0, 80);
  });
  if (Object.keys(captured).length === 0) return;
  try {
    const existing = JSON.parse(sessionStorage.getItem(UTM_KEY) || "{}");
    sessionStorage.setItem(UTM_KEY, JSON.stringify({ ...existing, ...captured, first_seen_at: existing.first_seen_at || new Date().toISOString() }));
  } catch (e) { /* ignore */ }
}
export function loadUtm() {
  try {
    const raw = sessionStorage.getItem(UTM_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    return {
      utm_source: obj.utm_source,
      utm_medium: obj.utm_medium,
      utm_campaign: obj.utm_campaign,
      utm_content: obj.utm_content,
      utm_term: obj.utm_term,
      gclid: obj.gclid,
    };
  } catch (e) { return {}; }
}

// ---------- Google Consent Mode v2 ----------
export const DEFAULT_CONSENT = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
  functionality_storage: "granted",
  security_storage: "granted",
};

export function loadConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) { return null; }
}

export function saveConsent(consent) {
  const record = {
    ...consent,
    version: "1.0",
    accepted_at: new Date().toISOString(),
    ua: (typeof navigator !== "undefined" && navigator.userAgent) ? navigator.userAgent.slice(0, 200) : "",
  };
  try { localStorage.setItem(CONSENT_KEY, JSON.stringify(record)); } catch (e) { /* ignore */ }
  return record;
}

export function applyConsent(consent) {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;
  window.gtag("consent", "update", {
    ad_storage: consent.ad_storage,
    ad_user_data: consent.ad_user_data,
    ad_personalization: consent.ad_personalization,
    analytics_storage: consent.analytics_storage,
  });
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: "consent_updated", ...consent });
}
